import { useGetSellerDeliveryDetails } from '@/hooks/sellers/useGetDeliveryDetails';
import { sellerSettingsResponseToDeliveryConfigurationUi } from '@/hooks/sellers/useGetDeliveryDetails/mapper';
import { useUpdateDeliveryConfiguration } from '@/hooks/sellers/useUpdateDeliveryConfiguration';
import { Dayjs } from 'dayjs';
import { WritableDraft } from 'immer';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import {
  DeliveryConfigurationUi,
  initialState,
  WeekDayDeliveryUi,
} from './DeliveryConfigurationUi';

export function useDeliveryConfigurationViewController() {
  const [uiState, setUiState] = useImmer<DeliveryConfigurationUi>(initialState);
  const sellerId = useParams().sellerId;
  const savedDataUiRef = useRef<DeliveryConfigurationUi>(uiState);
  const getDeliveryDetailsHook = useGetSellerDeliveryDetails(sellerId);
  const updateDeliveryDetailsHook = useUpdateDeliveryConfiguration();

  useEffect(() => {
    if (!getDeliveryDetailsHook.data) return;
    const newState = sellerSettingsResponseToDeliveryConfigurationUi(
      uiState,
      getDeliveryDetailsHook.data
    );
    savedDataUiRef.current = newState;
    setUiState(newState);
  }, [getDeliveryDetailsHook.data]);

  const onCutoffTimeChanged = (newTime: Dayjs | null) =>
    setUiState((draft) => {
      draft.nextDayCutOffTime = newTime;
      draft.cancelDisabled = false;
      calculateContinueState(draft);
    });

  const onNextDayTimeSelected = (newValue: string) =>
    setUiState((draft) => {
      draft.timePeriodSelected = newValue;
      draft.cancelDisabled = false;
      calculateContinueState(draft);
    });

  const onDeliveryDayCheckChanged = (
    deliveryDayId: string,
    newValue: boolean
  ) =>
    setUiState((draft) => {
      const deliveryDay = draft.deliveryDays.find(
        (deliveryDay) => deliveryDay.dayId === deliveryDayId
      );
      if (deliveryDay) {
        deliveryDay.enabled = newValue;
      }
      draft.cancelDisabled = false;
      calculateContinueState(draft);
    });

  const onMinDeliveryTimeChanged = (
    deliveryDayId: string,
    newTime: Dayjs | null
  ) =>
    setUiState((draft) => {
      const deliveryDay = draft.deliveryDays.find(
        (deliveryDay) => deliveryDay.dayId === deliveryDayId
      );
      if (!deliveryDay) return;
      deliveryDay.minDeliveryTime = newTime;
      draft.cancelDisabled = false;
      validateTimes(deliveryDay);
      calculateContinueState(draft);
    });

  const onMaxDeliveryTimeChanged = (
    deliveryDayId: string,
    newTime: Dayjs | null
  ) =>
    setUiState((draft) => {
      const deliveryDay = draft.deliveryDays.find(
        (deliveryDay) => deliveryDay.dayId === deliveryDayId
      );
      if (!deliveryDay) return;

      deliveryDay.maxDeliveryTime = newTime;
      draft.cancelDisabled = false;
      validateTimes(deliveryDay);
      calculateContinueState(draft);
    });

  const validateTimes = (deliveryDay: WritableDraft<WeekDayDeliveryUi>) => {
    if (!deliveryDay?.minDeliveryTime) return;
    if (!deliveryDay?.maxDeliveryTime) return;

    const invalidDate =
      deliveryDay.maxDeliveryTime <= deliveryDay.minDeliveryTime;

    deliveryDay.errorMessage = invalidDate
      ? 'La hora mínima debe ser menor a la hora máxima.'
      : undefined;
  };

  const calculateContinueState = (
    draft: WritableDraft<DeliveryConfigurationUi>
  ) => {
    draft.saveDisabled =
      draft.nextDayCutOffTime === null ||
      draft.timePeriodSelected.trim() === '' ||
      !deliveryDaysFilled(draft);
  };

  const deliveryDaysFilled = (
    draft: WritableDraft<DeliveryConfigurationUi>
  ): boolean => {
    for (const deliveryDay of draft.deliveryDays) {
      if (!deliveryDay.enabled) continue;
      if (deliveryDay.errorMessage) return false;
      if (deliveryDay.maxDeliveryTime === null) return false;
      if (deliveryDay.minDeliveryTime === null) return false;
    }
    return true;
  };

  const onClickedCancel = () => setUiState(savedDataUiRef.current);

  const onClickedSave = () => {
    if (!sellerId) return;
    updateDeliveryDetailsHook.updateDeliveryDetails(sellerId, uiState);
  };

  return {
    uiState,
    actions: {
      onCutoffTimeChanged,
      onNextDayTimeSelected,
      onDeliveryDayCheckChanged,
      onMinDeliveryTimeChanged,
      onMaxDeliveryTimeChanged,
      onClickedCancel,
      onClickedSave,
    },
  };
}
